import { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useTheme } from '../../../context/ThemeContext';
import { FiEye, FiFileText, FiTrendingUp } from 'react-icons/fi';

const BlogAnalytics = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const q = query(collection(db, 'blog_posts'), orderBy('views', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPosts(data);
    } catch (error) {
      console.error('Erro ao buscar dados do blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalViews = posts.reduce((acc, post) => acc + (post.views || 0), 0);
  const totalPosts = posts.length;
  const publishedPosts = posts.filter(p => p.status === 'published').length;
  
  if (loading) return <div className="p-8 text-center">Carregando relatórios...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Relatórios do Blog
        </h2>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Acompanhe o desempenho, as visualizações e o engajamento dos seus artigos.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`p-6 rounded-lg border flex items-center gap-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-4 rounded-full ${isDarkMode ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
            <FiEye className="w-6 h-6" />
          </div>
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Visualizações Totais</p>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{totalViews}</p>
          </div>
        </div>

        <div className={`p-6 rounded-lg border flex items-center gap-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-4 rounded-full ${isDarkMode ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
            <FiTrendingUp className="w-6 h-6" />
          </div>
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Média de Views / Post</p>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {totalPosts > 0 ? Math.round(totalViews / totalPosts) : 0}
            </p>
          </div>
        </div>

        <div className={`p-6 rounded-lg border flex items-center gap-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-4 rounded-full ${isDarkMode ? 'bg-sky-900/50 text-sky-400' : 'bg-sky-100 text-sky-600'}`}>
            <FiFileText className="w-6 h-6" />
          </div>
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Artigos Publicados</p>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{publishedPosts} / {totalPosts}</p>
          </div>
        </div>
      </div>

      {/* Top posts list */}
      <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Artigos Mais Acessados</h3>
        </div>
        <table className="w-full text-left">
          <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
            <tr>
              <th className={`px-6 py-3 text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Título</th>
              <th className={`px-6 py-3 text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Categoria</th>
              <th className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Visualizações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {posts.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  Nenhum artigo encontrado.
                </td>
              </tr>
            ) : (
              posts.map(post => (
                <tr key={post.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="max-w-sm md:max-w-md lg:max-w-lg break-words whitespace-normal">
                      {post.title}
                    </div>
                  </td>
                  <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {post.categoryName || 'Sem categoria'}
                  </td>
                  <td className={`px-6 py-4 text-right font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                    {post.views || 0}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BlogAnalytics;
